import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAppContext } from '../context/AppContext';
import { useToast } from './Toast';
import type { Song, ApiError } from '../types';

const MIN_TEMPO = 20;
const MAX_TEMPO = 300;
const MIN_LENGTH_SECONDS = 1;
const MAX_LENGTH_SECONDS = 3600; // 60 minutes

interface FormErrors {
  tempo?: string;
  length?: string;
}

function validateTempo(value: string): string | undefined {
  if (value === '') return undefined; // optional field
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return 'Tempo must be a whole number.';
  if (n < MIN_TEMPO) return `Tempo must be at least ${MIN_TEMPO} BPM.`;
  if (n > MAX_TEMPO) return `Tempo must be at most ${MAX_TEMPO} BPM.`;
  return undefined;
}

function validateLength(minutes: string, seconds: string): string | undefined {
  const m = minutes === '' ? 0 : Number(minutes);
  const s = seconds === '' ? 0 : Number(seconds);
  if (!Number.isFinite(m) || !Number.isFinite(s)) return 'Length values must be numbers.';
  if (m < 0 || s < 0) return 'Length values cannot be negative.';
  if (!Number.isInteger(m) || !Number.isInteger(s)) return 'Length values must be whole numbers.';
  if (s > 59) return 'Seconds must be between 0 and 59.';
  const total = m * 60 + s;
  if (total === 0) return undefined; // both blank / zero means unset
  if (total < MIN_LENGTH_SECONDS) return 'Song length must be at least 1 second.';
  if (total > MAX_LENGTH_SECONDS) return 'Song length must be at most 60 minutes.';
  return undefined;
}

export default function SongConfiguration() {
  const { songId } = useParams<{ songId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useAppContext();
  const { addToast } = useToast();

  const [song, setSong] = useState<Song | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Form fields
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [tempo, setTempo] = useState('');
  const [minutes, setMinutes] = useState('');
  const [seconds, setSeconds] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});

  // Fetch song on mount
  useEffect(() => {
    if (!songId) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setFetchError(null);
      try {
        const data = await apiClient.getSong(songId!);
        if (!cancelled) {
          setSong(data);
          setTitle(data.title ?? '');
          setArtist(data.artist ?? '');
          setTempo(data.tempo != null ? String(data.tempo) : '');
          setMinutes(data.lengthMinutes != null ? String(data.lengthMinutes) : '');
          setSeconds(data.lengthSeconds != null ? String(data.lengthSeconds) : '');
        }
      } catch (err: unknown) {
        if (!cancelled) setFetchError((err as ApiError)?.message ?? 'Failed to load song.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [songId]);

  const validate = useCallback((): boolean => {
    const next: FormErrors = {};
    next.tempo = validateTempo(tempo);
    next.length = validateLength(minutes, seconds);
    setErrors(next);
    return !next.tempo && !next.length;
  }, [tempo, minutes, seconds]);

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    setFeedback(null);
    if (!validate() || !songId) return;

    setSaving(true);
    try {
      const updates: Record<string, number | string | undefined> = {};
      updates.title = title.trim() || undefined;
      updates.artist = artist.trim() || undefined;
      updates.tempo = tempo !== '' ? Number(tempo) : undefined;
      const m = minutes !== '' ? Number(minutes) : 0;
      const s = seconds !== '' ? Number(seconds) : 0;
      if (m > 0 || s > 0) {
        updates.lengthMinutes = m;
        updates.lengthSeconds = s;
      } else {
        updates.lengthMinutes = undefined;
        updates.lengthSeconds = undefined;
      }

      const updated = await apiClient.updateSong(songId, updates);
      setSong(updated);
      dispatch({ type: 'UPDATE_SONG', payload: updated });
      setFeedback({ type: 'success', message: 'Song properties saved.' });
      addToast('Song properties saved.', 'success');
    } catch (err: unknown) {
      const msg = (err as ApiError)?.message ?? 'Failed to save changes.';
      setFeedback({ type: 'error', message: msg });
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  }, [songId, title, artist, tempo, minutes, seconds, validate, dispatch, addToast]);

  if (loading) return <div className="page"><p className="loading-spinner">Loading song…</p></div>;
  if (fetchError) return <div className="page"><p className="song-config-error" role="alert">{fetchError}</p></div>;
  if (!song) return <div className="page"><p>Song not found.</p></div>;

  return (
    <div className="page">
      <h2>Edit Song: {song.title || 'Untitled'}</h2>

      <form className="song-config-form" onSubmit={handleSubmit} noValidate>
        {/* Title */}
        <div className="form-group">
          <label htmlFor="song-title">Title</label>
          <input
            id="song-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Song title"
            className="song-text-input"
          />
        </div>

        {/* Artist */}
        <div className="form-group">
          <label htmlFor="song-artist">Artist</label>
          <input
            id="song-artist"
            type="text"
            value={artist}
            onChange={(e) => setArtist(e.target.value)}
            placeholder="Artist name"
            className="song-text-input"
          />
        </div>

        {/* Tempo */}
        <div className="form-group">
          <label htmlFor="tempo">Tempo (BPM)</label>
          <input
            id="tempo"
            type="number"
            min={MIN_TEMPO}
            max={MAX_TEMPO}
            step={1}
            value={tempo}
            onChange={(e) => { setTempo(e.target.value); setErrors((p) => ({ ...p, tempo: undefined })); }}
            aria-describedby={errors.tempo ? 'tempo-error' : undefined}
            aria-invalid={!!errors.tempo}
            placeholder={`${MIN_TEMPO}–${MAX_TEMPO}`}
          />
          {errors.tempo && <p id="tempo-error" className="field-error" role="alert">{errors.tempo}</p>}
        </div>

        {/* Length */}
        <fieldset className="form-group">
          <legend>Song Length</legend>
          <div className="length-inputs">
            <div className="length-field">
              <label htmlFor="length-min">Minutes</label>
              <input
                id="length-min"
                type="number"
                min={0}
                max={60}
                step={1}
                value={minutes}
                onChange={(e) => { setMinutes(e.target.value); setErrors((p) => ({ ...p, length: undefined })); }}
                aria-describedby={errors.length ? 'length-error' : undefined}
                aria-invalid={!!errors.length}
                placeholder="0"
              />
            </div>
            <div className="length-field">
              <label htmlFor="length-sec">Seconds</label>
              <input
                id="length-sec"
                type="number"
                min={0}
                max={59}
                step={1}
                value={seconds}
                onChange={(e) => { setSeconds(e.target.value); setErrors((p) => ({ ...p, length: undefined })); }}
                aria-describedby={errors.length ? 'length-error' : undefined}
                aria-invalid={!!errors.length}
                placeholder="0"
              />
            </div>
          </div>
          {errors.length && <p id="length-error" className="field-error" role="alert">{errors.length}</p>}
        </fieldset>

        {/* Feedback */}
        {feedback && (
          <p className={feedback.type === 'success' ? 'song-config-success' : 'song-config-error'} role="status">
            {feedback.message}
          </p>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/songs')}>
            Back to Library
          </button>
        </div>
      </form>
    </div>
  );
}
